import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAt, faEnvelope, faPhone } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

import SEO, { pageSEO } from "../components/seo.component";
import constants from "../constants/constants";
import { useUserParams } from "../hooks/useUserParams";

export default function About() {
  const seoConfig = pageSEO.about();
  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage,
    getCurrentAbout,
    getCurrentHeadshot,
  } = useUserParams();

  return (
    <>
      <SEO {...seoConfig} />

      <section className="relative overflow-hidden py-32 bg-gradient-to-br from-white via-cottage-secondary/20 to-white">
        <div className="relative mx-auto max-w-[1600px] px-6 lg:px-16">

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: .7 }}
            className="my-16"
          >

            <p className="uppercase tracking-[0.5em] font-semibold text-cottage-accent">
              About Me
            </p>

            <h2 className="mt-5 max-w-4xl font-heading text-5xl leading-tight text-cottage-primary lg:text-6xl">
              Helping Families
              <br />
              Find A Place
              <br />
              To Call Home.
            </h2>

          </motion.div>

          {/* Grid */}
          <div className="grid gap-6 lg:grid-cols-12">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, scale: .95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: .8 }}
              className="relative overflow-hidden rounded-lg lg:col-span-5 lg:row-span-2"
            >
              <img
                src={getCurrentHeadshot()}
                alt={getCurrentAgentName()}
                className="h-[700px] w-full object-cover transition duration-700 hover:scale-105"
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-cottage-primary/50 via-transparent to-transparent" />

              {/* Name Card */}
              <div className="absolute bottom-8 left-8 right-8 rounded-lg bg-white/90 p-6 backdrop-blur">
                <p className="uppercase tracking-[0.3em] text-sm text-cottage-primary">
                  {getCurrentAgentTitle()}
                </p>

                <h3 className="mt-2 font-heading text-3xl text-cottage-primary">
                  {getCurrentAgentName()}
                </h3>

                <p className="mt-2 text-cottage-muted">
                  {getCurrentBrokerage()}
                </p>
              </div>

            </motion.div>

            {/* About Text */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: .8 }}
              className="rounded-lg bg-white p-8 shadow-sm lg:col-span-7 border border-cottage-border"
            >
              <p className="text-lg leading-9 text-cottage-muted whitespace-pre-line mb-5">
                {getCurrentAbout()}
              </p>

              {/* Contact Details */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="rounded-lg bg-white p-5 shadow-sm lg:col-span-7 bg-cottage-secondary/20"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  {/* Phone */}
                  <div className="group flex items-start gap-4 rounded-lg border border-cottage-border p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">

                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-cottage-secondary transition group-hover:bg-cottage-accent">
                      <FontAwesomeIcon
                        icon={faPhone}
                        className="text-cottage-accent group-hover:text-white"
                      />
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-widest text-cottage-muted">
                        Phone
                      </p>

                      <p className="mt-2 font-medium text-cottage-primary">
                        {getCurrentAgentNumber()}
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="group flex items-start gap-4 rounded-lg border border-cottage-border p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">

                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-cottage-secondary transition group-hover:bg-cottage-accent">
                      <FontAwesomeIcon
                        icon={faEnvelope}
                        className="text-cottage-accent group-hover:text-white"
                      />
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-widest text-cottage-muted">
                        Email
                      </p>

                      <p className="mt-2 break-all font-medium text-cottage-primary">
                        {getCurrentAgentEmail()}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Stats */}
            <div className="grid gap-6 sm:grid-cols-3 lg:col-span-7">
              <motion.div
                whileHover={{ y: -8 }}
                className="rounded-lg border border-cottage-border bg-white p-8"
              >
                <h3 className="font-heading text-5xl text-cottage-accent">
                  15+
                </h3>

                <p className="mt-3 text-sm uppercase tracking-widest text-cottage-primary">
                  Years Experience
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -8 }}
                className="rounded-lg border border-cottage-border bg-white p-8"
              >
                <h3 className="font-heading text-5xl text-cottage-accent">
                  250+
                </h3>

                <p className="mt-3 text-sm uppercase tracking-widest text-cottage-primary">
                  Homes Sold
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -8 }}
                className="rounded-lg bg-cottage-primary p-8 text-white"
              >
                <h3 className="font-heading text-5xl text-cottage-highlight">
                  100%
                </h3>

                <p className="mt-3 text-sm uppercase tracking-widest">
                  Client Focus
                </p>
              </motion.div>
            </div>

            {/* Philosophy */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: .8 }}
              className="rounded-lg bg-cottage-secondary p-10 lg:col-span-7"
            >
              <p className="uppercase tracking-[0.4em] text-sm text-cottage-primary">
                My Philosophy
              </p>

              <h3 className="mt-5 max-w-3xl font-heading text-4xl leading-tight text-cottage-primary">
                "Real estate is not only about properties.
                It's about people, memories, and finding the right place."
              </h3>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}